import { useCallback, useEffect, useMemo, useState, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { sortBy, isEqual } from 'lodash'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { transactionFilterToQueryUrlService } from '@/services/transactionFilterToQueryUrlService'
import { getTransactionCategoryOptions } from '@/features/reports/reports.selectors'
import {
  clearTransactionFilters,
  updateTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import { fetchFilteredUserTransactions } from './transactions.slice'
import { usePrevious, useReselector } from '@/utils/sharedHooks'
import { selectFinancialAccounts } from '@/selectors/financeSelectors'
import { formatAccountNameWithInstitution } from './helpers'
import { Text, Select, Separator, FilterToggle } from '@/ui'
import DatePicker from '@/componentsV1/BaseComponents/DatePicker'
import { TypographyStyles, TextColors } from '@/styles'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '@/utils/deviceWidthHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'

interface Props {
  transactionsType?: 'expenses' | 'income'
  filterable?: boolean
}

export const UserTransactionsFilter = memo(
  ({ transactionsType, filterable }: Props) => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const defaultCat = searchParams.get('categoryId')
    const paramStartDate = searchParams.get('startDate')
    const paramEndDate = searchParams.get('endDate')
    const [showFilters, setShowFilters] = useState(Boolean(defaultCat))

    const categories = useReselector(
      getTransactionCategoryOptions,
      transactionsType,
      true
    )
    const transactionFilters = useReselector(
      (state) => state.transactionFilters
    )
    const prevTransactionFilters = usePrevious(transactionFilters)
    const financialAccounts = useReselector(selectFinancialAccounts)
    const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
    const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
    const currentUser = useReselector(getCurrentUser)

    useEffect(() => {
      if (!filterable) return
      dispatch(
        clearTransactionFilters({ limit: isMobileOrTablet ? '10' : '100' })
      )
      if (defaultCat) {
        dispatch(
          updateTransactionFilters({ transactionCategoryId: defaultCat })
        )
      }
      if (paramStartDate) {
        dispatch(updateTransactionFilters({ startDate: paramStartDate }))
      }
      if (paramEndDate) {
        dispatch(updateTransactionFilters({ endDate: paramEndDate }))
      }
    }, [
      defaultCat,
      dispatch,
      filterable,
      isMobileOrTablet,
      paramEndDate,
      paramStartDate,
    ])

    const fADropdownOptions = useMemo(() => {
      return sortBy(
        Object.values(financialAccounts).map((account) => ({
          text: formatAccountNameWithInstitution(account),
          value: String(account.id),
        })),
        'text'
      )
    }, [financialAccounts])

    useEffect(() => {
      dispatch(fetchTransactionCategoriesIfNeeded())
    }, [dispatch])

    const fetchTransactions = useCallback(() => {
      if (currentUser?.id) {
        dispatch(
          fetchFilteredUserTransactions(
            transactionFilterToQueryUrlService({
              ...transactionFilters,
              userId: currentUser.id,
            })
          )
        )
      }
    }, [dispatch, transactionFilters, currentUser?.id])

    useEffect(() => {
      if (!prevTransactionFilters) return
      if (!isEqual(transactionFilters, prevTransactionFilters)) {
        fetchTransactions()
      }
    }, [transactionFilters, prevTransactionFilters, fetchTransactions])

    const handleDateRangeChange = useCallback(
      ([startDate, endDate]: [string, string]) => {
        searchParams.delete('startDate')
        searchParams.delete('endDate')
        setSearchParams(searchParams)
        dispatch(updateTransactionFilters({ startDate, endDate }))
      },
      [dispatch, searchParams, setSearchParams]
    )

    const {
      startDate,
      endDate,
      transactionCategoryId,
      transactionsType: filterType,
      financialAccountId,
    } = transactionFilters

    if (!filterable) return null

    return (
      <div className="my-1">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="w-full sm:w-1/2 md:w-5/12">
            <DatePicker
              value={
                !startDate && !endDate
                  ? ''
                  : `${startDate || ''} - ${endDate || ''}`
              }
              onChange={handleDateRangeChange}
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              selectsRange
              clearable
              fullWidth
              placeholder="Filter by date"
            />
          </div>
          <div className="w-full sm:w-1/2 md:w-1/4 sm:text-right">
            <FilterToggle
              pressed={showFilters}
              onPressedChange={() => setShowFilters((val) => !val)}
              className="w-full sm:w-auto"
            >
              Filters
            </FilterToggle>
          </div>
        </div>

        {showFilters && (
          <>
            <Text
              variant={TypographyStyles.EYEBROW}
              color={TextColors.MUTED}
              className="mt-4"
            >
              Filter Transactions:
            </Text>
            <div
              className={`mt-3 ${
                isMobile
                  ? 'flex flex-col gap-3'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-4'
              }`}
            >
              <div className={isMobile ? '' : 'pr-0'}>
                <Select
                  placeholder="Category"
                  value={String(transactionCategoryId) || undefined}
                  options={categories.map((category) => ({
                    value: String(category.value),
                    text: String(category.text),
                  }))}
                  onValueChange={(value) =>
                    dispatch(
                      updateTransactionFilters({
                        transactionCategoryId: value,
                      })
                    )
                  }
                  disabled={filterType === 'uncategorized'}
                  searchable
                  clearable
                />
              </div>
              <div className={isMobile ? '' : 'pr-0'}>
                <Select
                  placeholder="Account"
                  value={String(financialAccountId) || undefined}
                  options={fADropdownOptions}
                  onValueChange={(value) =>
                    dispatch(
                      updateTransactionFilters({
                        financialAccountId: Number(value),
                      })
                    )
                  }
                  clearable
                />
              </div>
            </div>
            <Separator />
          </>
        )}
      </div>
    )
  }
)

UserTransactionsFilter.displayName = 'UserTransactionsFilter'
